import type { CSSProperties, ReactNode } from 'react';
import { Delta } from '../yeni-design/primitives';

/**
 * Degisim rozeti ya da onun YERI. CLS (2026-08-29, 390px Slow 4G olculdu): rozet sonradan belirince kart itiyordu;
 * 21px alt satirin olculen yuksekligi. Iki donem de geldi ama kiyas yapilamiyorsa (onceki toplam yok/0) "-":
 * sahte %0 ya da %∞ basilmaz.
 */
export function DegisimRozeti({ deger, hazir, ondalik = 2 }: { deger: number | null; hazir: boolean; ondalik?: number }) {
  if (!hazir) return <span className="block h-[21px]" aria-hidden="true" data-testid="rozet-yeri" />;
  if (deger === null)
    return (
      <span className="block h-[21px] text-ink-400" data-testid="delta-yok" title="Önceki dönemde karşılaştırılacak veri yok">
        —
      </span>
    );
  return <Delta value={deger} decimals={ondalik} size="sm" />;
}

export interface OlcuKartiProps {
  testid: string;
  label: string;
  value: string;
  /** Sag alt: genelde `DegisimRozeti`. */
  degisim?: ReactNode;
  /** Sol alt: "Önceki ..." ya da "Bugün", "12 ürün" gibi kisa bilgi. */
  alt?: ReactNode;
  ek?: ReactNode;
  bilgi?: ReactNode;
  /** Ana olcu: genis ekranda 1,5 kat kolon ve buyuk sayi. */
  vurgulu?: boolean;
  secili: boolean;
  secilebilir?: boolean;
  onSec?: () => void;
  /** Tiklama alaninin ekran okuyucu etiketi. */
  secimEtiketi: string;
}

/**
 * Satis Analiz ve Stok Analizi'nin ust karti (kullanici, 2026-11-01). Kart secicidir: Satis'ta grafigin olcusunu,
 * Stok'ta sekmeyi secer. Tiklama alani karti kaplayan dugme; ⓘ dugmesi `relative z-10` ile onun ustunde (ic ice
 * dugme gecersiz HTML olurdu).
 *
 * Tutar KART genisligine sigar (kullanici, 2026-09-15): kart olcu kabidir (`container-type: inline-size`); tutar
 * `min(azami, (kart - 24px) / (karakter x 0.6))`: 24 px = yatay dolgu (2x12), 0.6 = olculen JetBrains Mono
 * karakter/em orani (0,596).
 */
export function OlcuKarti({
  testid, label, value, degisim, alt, ek, bilgi, vurgulu = false, secili, secilebilir = true, onSec, secimEtiketi,
}: OlcuKartiProps) {
  const olcu = {
    containerType: 'inline-size', '--sa-n': Math.max(value.length, 1), '--sa-azami': vurgulu ? '30px' : '20px',
  } as CSSProperties;
  const cerceve = secili ? 'border-ink-900' : 'border-border';
  return (
    <div
      data-testid={testid}
      style={olcu}
      className={`relative min-w-0 rounded-lg border bg-surface px-3 py-2.5 flex flex-col gap-1 ${cerceve} ${vurgulu ? 'sm:col-span-full lg:col-span-1' : ''}`}
    >
      <button
        type="button"
        data-testid={`${testid}-sec`}
        aria-pressed={secili}
        aria-label={secimEtiketi}
        disabled={!secilebilir}
        title={secilebilir ? undefined : 'Bu seçimde grafiği yok'}
        onClick={onSec}
        className="focus-ring absolute inset-0 rounded-lg enabled:cursor-pointer enabled:hover:bg-ink-100/40"
      />
      <span className="flex h-[18px] items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-ink-500">
        <span className="truncate">{label}</span>
        {bilgi && <span className="relative z-10 inline-flex">{bilgi}</span>}
      </span>
      <div className="mono font-semibold leading-tight text-ink-900 [font-size:min(var(--sa-azami),calc((100cqi_-_24px)/(var(--sa-n)*0.6)))]">
        {value}
      </div>
      <div className="flex min-h-[21px] items-center justify-between gap-x-2 text-[11px] text-ink-500">
        <div className="min-w-0">{alt}</div>
        {degisim && <div className="shrink-0">{degisim}</div>}
      </div>
      {ek && <div className="text-[11px] text-ink-500">{ek}</div>}
    </div>
  );
}
